import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inbox } from '@repletoryreach/sdk-api';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Stack, Button, Group, Text, Badge, Code } from '@repletoryreach/sdk-ui';
import { DataTable, TableShell, EmptyState, Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { LOCALES } from '../../../i18n/catalog';
import { formatDate } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';

const SHORTCUT_SHAPE = /^[a-z0-9][a-z0-9_.-]*$/;
const PAGE_SIZE = 50;
const MAX_BODY = 4096;

/** Mirror of the server normalisation so the form shows what will be stored. */
function normaliseShortcut(raw: string): string {
  return raw.trim().replace(/^\/+/, '').trim().toLowerCase();
}

function preview(body: string, n = 90): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length <= n ? flat : `${flat.slice(0, n - 1)}…`;
}

export default function SettingsCannedRepliesPage() {
  const t = useT();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 200);
  const [editing, setEditing] = useState<inbox.CannedReply | 'new' | null>(null);
  // §4 item 8: a multilingual library had no way to view one language at a time.
  const [language, setLanguage] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [debounced, language]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['canned-replies', 'list', debounced],
    queryFn: () => inbox.listCannedReplies({
      search: debounced.trim() || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      ...(language ? { language } : {})
    }),
    placeholderData: (prev) => prev
  });
  const rows = (data?.data ?? []) as Indexed<inbox.CannedReply>[];
  const total = data?.meta?.total ?? rows.length;

  // Prefix match also refreshes the inbox composer's picker cache.
  const invalidate = () => qc.invalidateQueries({ queryKey: ['canned-replies'] });

  const del = useMutation({
    mutationFn: (id: string) => inbox.deleteCannedReply(id),
    onSuccess: () => {
      invalidate();
      notifySuccess(t('canned.deleted'), t('canned.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('settings.cannedReplies.deleteFailed'), t('canned.title'))
  });

  return (
    <Stack gap="md">
      <TableShell
        title={t('canned.title')}
        description={t('canned.description')}
        totalLabel={total ? String(total) : undefined}
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t('canned.search')}
        // The shell owns a handful of words of its own (the view switcher, the
        // row-range line, the page-size options). They defaulted to English,
        // which is invisible to the coverage check because they live inside the
        // design system rather than this catalogue.
        labels={{
          table: t('table.view.table'),
          cards: t('table.view.cards'),
          list: t('table.view.list'),
          search: t('table.search'),
          perPage: t('table.perPage'),
          pageSizeOption: (n) => t('table.perPageOption', { n }),
          empty: t('table.noRecords'),
          range: (from, to, total) => t('table.range', { from, to, total })
        }}
        actions={(
          <Group gap="xs">
            {/* §4 item 8: a multilingual library had no way to be read one
                language at a time. */}
            <Select
              size="sm"
              aria-label={t('common.language')}
              value={language}
              onChange={(e) => setLanguage(e.currentTarget.value)}
              data={[
                { value: '', label: t('canned.language.any') },
                ...LOCALES.map((l) => ({ value: l.code, label: l.label }))
              ]}
            />
            <Button size="sm" onClick={() => setEditing('new')}>{t('canned.new')}</Button>
          </Group>
        )}
        loading={isLoading}
        error={error ? <Alert color="error">{t('settings.cannedReplies.loadError')}</Alert> : undefined}
        isEmpty={rows.length === 0}
        empty={(
          <EmptyState
            title={t('canned.empty')}
            description={search ? undefined : t('canned.emptyHint')}
            action={!search ? <Button size="sm" onClick={() => setEditing('new')}>{t('canned.new')}</Button> : undefined}
          />
        )}
      >
        <DataTable<Indexed<inbox.CannedReply>>
          records={rows}
          loading={isLoading}
          idAccessor="id"
          onRowClick={(r) => setEditing(r)}
          columns={[
            { accessor: 'shortcut', title: t('canned.shortcut'), render: (r) => <Code>/{r.shortcut}</Code> },
            { accessor: 'title', title: t('canned.titleField') },
            {
              accessor: 'body',
              title: t('canned.body'),
              render: (r) => (
                <Text role="bodySmall" c="var(--rr-color-text-muted)" style={{ maxWidth: 420 }}>{preview(r.body)}</Text>
              )
            },
            {
              accessor: 'language',
              title: t('canned.language'),
              render: (r) => <Badge size="xs" variant="light">{r.language}</Badge>
            },
            { accessor: 'updated_at', title: t('canned.updated'), render: (r) => formatDate(r.updated_at) },
            {
              accessor: 'actions',
              title: '',
              render: (r) => (
                <Group gap="xs" wrap="nowrap">
                  <Button size="xs" variant="subtle" onClick={(e) => { e.stopPropagation(); setEditing(r); }}>
                    {t('canned.edit')}
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    loading={del.isPending && del.variables === r.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      openConfirmModal({
                        title: `${t('action.delete')} /${r.shortcut}?`,
                        children: <p style={{ margin: 0 }}>{t('canned.deleteConfirm')}</p>,
                        labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                        confirmProps: { color: 'red' },
                        onConfirm: () => del.mutate(r.id)
                      });
                    }}
                  >
                    {t('action.delete')}
                  </Button>
                </Group>
              )
            }
          ]}
        />
      </TableShell>

      <CannedReplyDrawer
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={invalidate}
      />
    </Stack>
  );
}

function CannedReplyDrawer({
  target,
  onClose,
  onSaved
}: {
  target: inbox.CannedReply | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const isNew = target === 'new';
  const existing = isNew || target === null ? null : target;
  const open = target !== null;

  const [shortcut, setShortcut] = useState('');
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState('en');
  const [body, setBody] = useState('');
  const [shortcutError, setShortcutError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * §4 item 2: hydration was guarded by a `hydratedFor` marker that was only
   * cleared on a SUCCESSFUL save. Cancelling left it set, so reopening the
   * same row skipped rehydration and showed the edits the user had just
   * discarded. An effect keyed on the open row has no such asymmetry.
   */
  const key = existing?.id ?? (isNew ? 'new' : null);
  useEffect(() => {
    if (!open) return;
    setShortcut(existing?.shortcut ?? '');
    setTitle(existing?.title ?? '');
    setLanguage(existing?.language ?? 'en');
    setBody(existing?.body ?? '');
    setShortcutError(null);
  }, [open, key]); // eslint-disable-line react-hooks/exhaustive-deps

  /** §4 item 4: closing discarded up to 4096 characters with no warning. */
  const dirty = open && (
    shortcut !== (existing?.shortcut ?? '')
    || title !== (existing?.title ?? '')
    || body !== (existing?.body ?? '')
    || language !== (existing?.language ?? 'en')
  );

  function requestClose() {
    if (!dirty) { onClose(); return; }
    openConfirmModal({
      title: t('canned.discard.title'),
      children: <Text role="bodySmall">{t('canned.discard.body')}</Text>,
      labels: { confirm: t('canned.discard.confirm'), cancel: t('canned.discard.keep') },
      confirmProps: { color: 'red' },
      onConfirm: onClose
    });
  }

  const languageData: { value: string; label: string }[] = LOCALES.map((l) => ({ value: l.code, label: `${l.label} (${l.code})` }));
  if (language && !LOCALES.some((l) => l.code === language)) languageData.push({ value: language, label: language });

  const normalised = normaliseShortcut(shortcut);
  const shortcutInvalid = shortcut.trim() !== '' && !SHORTCUT_SHAPE.test(normalised);

  async function save() {
    if (!normalised || !body.trim()) return;   // the button is already disabled
    if (shortcutInvalid) {
      setShortcutError(t('canned.shortcutHint'));
      return;
    }
    setBusy(true);
    setShortcutError(null);
    try {
      const input = { shortcut: normalised, title: title.trim() || undefined, body, language };
      if (existing) await inbox.updateCannedReply(existing.id, input);
      else await inbox.createCannedReply(input);
      notifySuccess(
        existing
          ? t('canned.updated', { shortcut: normalised })
          : t('canned.created', { shortcut: normalised }),
        t('canned.title')
      );
      onSaved();
      onClose();
    } catch (e) {
      if (inbox.isShortcutTaken(e)) {
        // §4 item 11: this was shown twice, as a field error AND a toast. The
        // field is on screen, so inline is the right one.
        setShortcutError(t('canned.shortcutTaken'));
      } else {
        notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('canned.title'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer opened={open} onClose={requestClose} title={isNew ? t('canned.new') : t('canned.edit')} position="right" size="md">
      <Stack gap="md" style={{ padding: 4 }}>
        <TextInput
          label={t('canned.shortcut')}
          description={t('canned.shortcutHint')}
          leftSection={<span aria-hidden>/</span>}
          value={shortcut}
          onChange={(e) => { setShortcut(e.currentTarget.value); setShortcutError(null); }}
          error={shortcutError ?? (shortcutInvalid ? t('canned.shortcutHint') : undefined)}
          placeholder={t('settings.cannedReplies.shortcutPlaceholder')}
          required
          autoComplete="off"
          spellCheck={false}
        />
        <TextInput
          label={t('canned.titleField')}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          placeholder={normalised || undefined}
          maxLength={200}
        />
        <Select
          label={t('canned.language')}
          value={language}
          onChange={(e) => setLanguage(e.currentTarget.value)}
          data={languageData}
        />
        <Textarea
          label={t('canned.body')}
          description={t('canned.bodyHint')}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          minRows={6}
          maxLength={MAX_BODY}
          required
        />
        <Group justify="space-between" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
          <Text role="caption" c="var(--rr-color-text-muted)">{body.length}/{MAX_BODY}</Text>
          <Group gap="xs">
            <Button variant="default" onClick={requestClose}>{t('action.cancel')}</Button>
            <Button onClick={save} loading={busy} disabled={!normalised || !body.trim() || shortcutInvalid}>
              {t('action.save')}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Drawer>
  );
}
